// Chat Session Tracking Service
// This service tracks chat sessions for analytics purposes
import { API_URL } from '../common/Constants';

// Session state - use a single global session ID
let chatSessionId = localStorage.getItem('chatSessionId');
let heartbeatInterval = null;
const HEARTBEAT_INTERVAL = 60000; // 1 minute
const IDLE_TIMEOUT = 300000; // 5 minutes

// Debug flag
const DEBUG = false;

// User activity tracking - separate chat activity from general activity
let lastChatActivity = Date.now();
let chatActivityListeners = [];

/**
 * Debug logger
 */
function log(...args) {
  if (DEBUG) {
    console.log('[ChatSession]', ...args);
  }
}

/**
 * Start a new chat session
 * @param {string} tenantId - The tenant ID
 * @returns {Promise<string|null>} - The session ID or null if failed
 */
export async function startChatSession(tenantId) {
  // Don't create a new session if one already exists
  if (chatSessionId) {
    log('Using existing chat session:', chatSessionId);
    return chatSessionId;
  }

  try {
    log('Creating new chat session for tenant:', tenantId);

    // Use the fixed endpoint for more reliable session creation
    console.log('Creating session for tenant:', tenantId);
    const response = await fetch(`${API_URL}/chat-sessions/start-session/${tenantId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    chatSessionId = data.sessionId;

    // Store in localStorage for persistence
    localStorage.setItem('chatSessionId', chatSessionId);

    // Start heartbeat to keep session alive
    startHeartbeat();

    // Set up page unload handler
    window.addEventListener('beforeunload', handlePageUnload);

    // Record this as chat activity
    updateChatActivity();

    log('Started new chat session:', chatSessionId);
    return chatSessionId;
  } catch (error) {
    console.error('Error starting chat session:', error);
    return null;
  }
}

/**
 * End the current chat session
 * @param {string} reason - The reason for ending the session ('closed', 'timeout', 'abandoned')
 * @returns {Promise<void>}
 */
export async function endChatSession(reason = 'closed') {
  // Use the session ID from state or localStorage
  const sessionId = chatSessionId || localStorage.getItem('chatSessionId');

  if (!sessionId) {
    log('No session to end');
    return;
  }

  try {
    log('Ending chat session:', sessionId);

    // Use the fixed endpoint for more reliable session ending
    const response = await fetch(`${API_URL}/chat-sessions/end-session/${sessionId}`, {
      method: 'POST'
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Stop heartbeat
    stopHeartbeat();

    // Remove page unload handler
    window.removeEventListener('beforeunload', handlePageUnload);

    // Clear session ID
    chatSessionId = null;
    localStorage.removeItem('chatSessionId');

    log('Chat session ended:', sessionId);
  } catch (error) {
    console.error('Error ending chat session:', error);
  }
}

/**
 * Record a message in the current session
 * @param {string} tenantId - The tenant ID (optional, used if no session exists)
 * @returns {Promise<void>}
 */
export async function recordChatMessage(tenantId) {
  // Use the session ID from state or localStorage
  let sessionId = chatSessionId || localStorage.getItem('chatSessionId');

  // If no session exists, start one first
  if (!sessionId && tenantId) {
    log('No session exists, creating one first');
    await startChatSession(tenantId);
    sessionId = chatSessionId;
  }

  // If still no session, we can't record the message
  if (!sessionId) {
    log('No session ID available, cannot record message');
    return;
  }

  try {
    log('Recording message for session:', sessionId);

    // Use the fixed endpoint for more reliable message recording
    const response = await fetch(`${API_URL}/chat-sessions/record-message/${sessionId}`, {
      method: 'POST'
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    log('Message recorded, count:', data.messagesCount);

    // Update chat activity time when a message is recorded
    updateChatActivity();
  } catch (error) {
    console.error('Error recording message:', error);

    // If the session is not found, clear it
    if (error.message.includes('404') || error.message.includes('400')) {
      log('Session not found, clearing local session ID');
      chatSessionId = null;
      localStorage.removeItem('chatSessionId');
    }
  }
}

/**
 * Start heartbeat to keep session alive
 * @private
 */
function startHeartbeat() {
  // Clear any existing interval
  stopHeartbeat();

  // Set up new interval
  heartbeatInterval = setInterval(async () => {
    // Use the session ID from state or localStorage
    const sessionId = chatSessionId || localStorage.getItem('chatSessionId');

    if (!sessionId) {
      stopHeartbeat();
      return;
    }

    // Check if user is idle
    const now = Date.now();

    if (now - lastChatActivity > IDLE_TIMEOUT) {
      // User is idle, end the session
      await endChatSession('timeout');
      return;
    }

    // User is active, check session status
    try {
      const response = await fetch(`${API_URL}/chat-sessions/check-session/${sessionId}`);
      if (!response.ok) {
        // Session might have been ended on the server side
        stopHeartbeat();
        chatSessionId = null;
        localStorage.removeItem('chatSessionId');
      }
    } catch (error) {
      console.error('Heartbeat error:', error);
    }
  }, HEARTBEAT_INTERVAL);
}

/**
 * Stop the heartbeat interval
 * @private
 */
function stopHeartbeat() {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
}

/**
 * Handle page unload (user leaving the page)
 * @private
 */
function handlePageUnload(event) {
  // Use the session ID from state or localStorage
  const sessionId = chatSessionId || localStorage.getItem('chatSessionId');

  if (sessionId) {
    // Use sendBeacon for more reliable delivery during page unload
    navigator.sendBeacon(
      `${API_URL}/chat-sessions/end-session/${sessionId}`,
      ''
    );
  }
}

/**
 * Update chat activity timestamp
 * This is only called when the user interacts with the chat (typing, sending messages)
 */
function updateChatActivity() {
  lastChatActivity = Date.now();
  localStorage.setItem('lastChatActivity', lastChatActivity.toString());

  console.log('🔥 chatSessionService: Chat activity updated - DEBUGGING', new Date(lastChatActivity).toISOString());
  console.log('🔥 chatSessionService: Notifying', chatActivityListeners.length, 'listeners');

  // Notify all listeners
  chatActivityListeners.forEach((listener, index) => {
    try {
      console.log('🔥 chatSessionService: Calling listener', index);
      listener(lastChatActivity);
    } catch (e) {
      console.error('Error in chat activity listener:', e);
    }
  });

  log('Chat activity updated:', new Date(lastChatActivity).toISOString());
}

/**
 * Track chat activity - only call this for chat-specific interactions
 * like typing in the chat input or sending a message
 */
export function trackChatActivity() {
  console.log('🔥 chatSessionService: trackChatActivity called - DEBUGGING');
  updateChatActivity();
}

/**
 * Add a listener for chat activity
 * @param {Function} listener - Function to call when chat activity is detected
 */
export function addChatActivityListener(listener) {
  if (typeof listener === 'function' && !chatActivityListeners.includes(listener)) {
    chatActivityListeners.push(listener);
    log('Added chat activity listener, total listeners:', chatActivityListeners.length);
  }
}

/**
 * Remove a chat activity listener
 * @param {Function} listener - Function to remove
 */
export function removeChatActivityListener(listener) {
  const index = chatActivityListeners.indexOf(listener);
  if (index !== -1) {
    chatActivityListeners.splice(index, 1);
    log('Removed chat activity listener, remaining listeners:', chatActivityListeners.length);
  }
}

/**
 * Setup activity tracking
 */
export function setupActivityTracking() {
  // Set initial activity timestamp
  updateChatActivity();

  // Start heartbeat if we have a session
  if (chatSessionId || localStorage.getItem('chatSessionId')) {
    startHeartbeat();
    window.addEventListener('beforeunload', handlePageUnload);
  }
}

/**
 * Cleanup activity tracking
 */
export function cleanupActivityTracking() {
  // Clear all activity listeners
  chatActivityListeners = [];
  log('Cleared all chat activity listeners');
}

/**
 * Get the current session ID
 * @returns {string|null} - The current session ID or null if no session exists
 */
export function getCurrentSessionId() {
  return chatSessionId || localStorage.getItem('chatSessionId');
}

/**
 * Get the time of last chat activity
 * @returns {number} - Timestamp of last chat activity
 */
export function getLastChatActivity() {
  return lastChatActivity;
}

/**
 * Reset the last chat activity time to now
 * This is useful for testing idle detection
 */
export function resetLastChatActivity() {
  updateChatActivity();
  log('Last chat activity time reset');
}

// Initialize - clear any stale session on page load
(function () {
  if (chatSessionId) {
    log('Found existing session:', chatSessionId);
    // Verify the session is still active
    fetch(`${API_URL}/chat-sessions/check-session/${chatSessionId}`)
      .then(response => {
        if (!response.ok) {
          log('Existing session is not active, clearing');
          chatSessionId = null;
          localStorage.removeItem('chatSessionId');
        } else {
          log('Existing session is active');
        }
      })
      .catch(error => {
        log('Error checking session:', error);
      });
  }

  // Initialize lastChatActivity from localStorage if available
  const storedActivity = localStorage.getItem('lastChatActivity');
  if (storedActivity) {
    lastChatActivity = parseInt(storedActivity, 10);
    log('Loaded last chat activity from localStorage:', new Date(lastChatActivity).toISOString());
  }
})();